/**
 * GuapBoard - Real-time leaderboard widget for Dashboard
 * Shows envelope challenge progress, streak, and savings
 * Includes progress bars showing goal completion
 */
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEnvelope } from '@/contexts/EnvelopeContext';

// Progress Bar Component - Similar to Guap Meter style
function ProgressBar({ current, goal, withdrawn = 0, showWithdrawn = false }) {
  const progress = goal > 0 ? Math.min((current / goal) * 100, 100) : 0;
  const withdrawnProgress = goal > 0 ? Math.min((withdrawn / goal) * 100, 100) : 0;
  
  return (
    <div className="w-full">
      <div className="relative h-2 bg-white/10 rounded-full overflow-hidden">
        {/* Withdrawn indicator (shows what was lost) */}
        {showWithdrawn && withdrawn > 0 && (
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(progress + withdrawnProgress, 100)}%` }}
            className="absolute top-0 left-0 h-full bg-red-500/30 rounded-full"
          />
        )}
        {/* Current progress */}
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full"
        />
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-xs text-purple-300">${Math.round(current)}</span>
        <span className="text-xs text-purple-300">${Math.round(goal)}</span>
      </div>
    </div>
  );
}

export function GuapBoard() {
  const { 
    hasActiveChallenge, 
    todayEnvelope, 
    myProgress, 
    guapBoard,
    canSaveToday,
    challengeGoal
  } = useEnvelope();

  if (!hasActiveChallenge) {
    return (
      <div className="bg-gradient-to-br from-purple-900 to-indigo-900 rounded-2xl p-5 text-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <span>🏆</span> Guap Board
          </h3>
        </div>
        <div className="text-center py-6">
          <p className="text-purple-200 mb-4">Start an Envelope Challenge to see your progress here!</p>
          <Link 
            to="/app/envelope-v2"
            className="inline-block px-4 py-2 bg-yellow-500 text-black font-bold rounded-lg hover:bg-yellow-400 transition-colors"
          >
            Start Challenge →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="bg-gradient-to-br from-purple-900 to-indigo-900 rounded-2xl p-5 text-white overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg flex items-center gap-2">
          <span>🏆</span> Guap Board
        </h3>
        <Link 
          to="/app/envelope-v2"
          className="text-xs text-purple-300 hover:text-white transition-colors"
        >
          View Challenge →
        </Link>
      </div>

      {/* Today's Envelope Status */}
      {todayEnvelope && (
        <div className={`mb-4 p-4 rounded-xl ${
          todayEnvelope.status === 'saved' 
            ? 'bg-green-500/20 border border-green-500/30' 
            : todayEnvelope.status === 'skipped'
              ? 'bg-yellow-500/20 border border-yellow-500/30'
              : 'bg-white/10 border border-white/20'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-purple-200 uppercase tracking-wide">Today's Envelope</p>
              <p className="text-2xl font-black mt-1">
                ${Math.round(todayEnvelope.amount)}
              </p>
            </div>
            <div className="text-right">
              {todayEnvelope.status === 'saved' && (
                <span className="px-3 py-1 bg-green-500 text-white text-sm font-bold rounded-full">
                  ✓ SAVED
                </span>
              )}
              {todayEnvelope.status === 'skipped' && (
                <span className="px-3 py-1 bg-yellow-500 text-black text-sm font-bold rounded-full">
                  🔒 VAULT
                </span>
              )}
              {todayEnvelope.status === 'pending' && (
                <Link
                  to="/app/envelope-v2"
                  className="px-3 py-1 bg-yellow-500 text-black text-sm font-bold rounded-full hover:bg-yellow-400 inline-block"
                >
                  SAVE NOW →
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* My Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-black text-green-400">${Math.round(myProgress.totalSaved)}</p>
          <p className="text-xs text-purple-200">Saved</p>
        </div>
        <div className="bg-white/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-black text-yellow-400">{myProgress.currentStreak}</p>
          <p className="text-xs text-purple-200">Streak 🔥</p>
        </div>
        <div className="bg-white/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-black text-purple-300">${Math.round(myProgress.vaultAmount)}</p>
          <p className="text-xs text-purple-200">Vault 🔒</p>
        </div>
      </div>

      {/* Leaderboard */}
      {guapBoard.length > 0 && (
        <div>
          <p className="text-xs text-purple-300 uppercase tracking-wide mb-2">Leaderboard</p>
          <div className="space-y-3">
            {guapBoard.slice(0, 5).map((player, index) => (
              <motion.div
                key={player.user_id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`p-3 rounded-xl ${
                  player.is_self ? 'bg-yellow-500/20 border border-yellow-500/30' : 'bg-white/5'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-lg w-6 text-center">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}`}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-sm flex items-center gap-1">
                      {player.is_self ? 'You' : player.name?.split(' ')[0] || 'Player'}
                      {player.has_pending_catchup && (
                        <span className="text-xs bg-orange-500/30 text-orange-300 px-1.5 py-0.5 rounded-full">
                          Catch Up
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-green-400 font-bold text-sm">${Math.round(player.total_saved)}</p>
                    <p className="text-xs text-purple-300">{player.current_streak}🔥</p>
                  </div>
                </div>
                {/* Progress Bar under each player */}
                <ProgressBar 
                  current={player.total_saved || 0} 
                  goal={player.goal || challengeGoal || 500}
                  withdrawn={player.total_withdrawn || 0}
                  showWithdrawn={player.has_pending_catchup}
                />
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default GuapBoard;
